import { useEffect, useRef } from "react";
import { playSfx, fadeSfx } from "@/lib/sfx";
import { glitchHit, impact, thump } from "@/lib/fx";

const GLITCHES = ["glitch_a", "glitch_b", "glitch_c"];

// Drives the boot soundtrack: drone bed, riser, accelerating heartbeat, line stingers, braam, hum.
export default function useBootSound(stage, count, total) {
  const h = useRef({});
  const gen = useRef(0);
  const progress = useRef(0);
  progress.current = count / total;

  const track = (key, p) => {
    const g = gen.current;
    return p.then((handle) => {
      if (g !== gen.current) fadeSfx(handle, 0.05);
      else h.current[key] = handle;
      return handle;
    });
  };

  useEffect(() => {
    gen.current += 1;
    track("drone", playSfx("boot_drone", { gain: 0.5, fadeIn: 2.5, loop: true }));
    track("riser", playSfx("boot_riser", { gain: 0.75, fadeIn: 1.2 }));
    return () => {
      gen.current += 1;
      Object.values(h.current).forEach((x) => fadeSfx(x, 0.2));
      h.current = {};
    };
  }, []);

  useEffect(() => {
    if (stage !== "log") return;
    let t;
    const beat = () => {
      const p = progress.current;
      playSfx("heartbeat", { gain: 0.4 + p * 0.5 });
      thump();
      t = setTimeout(beat, 1150 - p * 800);
    };
    t = setTimeout(beat, 900);
    return () => clearTimeout(t);
  }, [stage]);

  const onLine = (type, index) => {
    if (type === "warn") {
      playSfx("warn_sting", { gain: 0.65 });
      glitchHit(true);
    } else if (type === "dump" || index % 4 === 0) {
      playSfx(GLITCHES[index % 3], { gain: 0.3 });
      glitchHit(false);
    }
  };

  const onInitiating = () => {
    fadeSfx(h.current.riser, 0.06);
    track("braam", playSfx("initiating_braam", { gain: 1 })).then(() => impact(2));
  };

  const onEstablished = () => {
    fadeSfx(h.current.drone, 0.6);
    fadeSfx(h.current.braam, 0.9);
    track("hum", playSfx("established_hum", { gain: 0.45 })).then((x) => setTimeout(() => fadeSfx(x, 0.9), 1300));
  };

  return { onLine, onInitiating, onEstablished };
}
